import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, type Project } from "../api/client";
import Icon from "../components/Icon";
import { setCurrentProject } from "../hooks/useCurrentProject";

export default function StudyDocumentPage() {
  const { studyId } = useParams();
  const navigate = useNavigate();
  const numericStudyId = studyId ? Number(studyId) : null;
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const study = useMemo(
    () => projects.find((project) => project.id === numericStudyId) ?? null,
    [numericStudyId, projects],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .listProjects(true)
      .then((next) => {
        if (cancelled) return;
        setProjects(next);
        if (numericStudyId != null) setCurrentProject({ kind: "project", id: numericStudyId });
      })
      .catch((e) => {
        if (!cancelled) setError(String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [numericStudyId]);

  return (
    <>
      <div className="main-header document-header">
        <button type="button" className="ghost" onClick={() => navigate(study ? `/studies/${study.id}` : "/studies")}>
          Studies
        </button>
        <div>
          <span className="crumb">/ studies / {study?.name ?? "study"} / deliverable</span>
          <h2>Deliverable</h2>
        </div>
      </div>

      <div className="main-body">
        {loading && <p className="muted">Loading...</p>}
        {error && <p className="error">{error}</p>}
        {!loading && !error && (
          <section className="empty-study-state document-empty-state">
            <span className="empty-state-icon" aria-hidden="true">
              <Icon name="doc" size={18} />
            </span>
            <h3>No saved deliverable document</h3>
            <p className="muted">
              This study does not have a saved deliverable document yet. Start from the study workspace,
              a chat, or a task run; generated outputs will appear here once they are saved.
            </p>
            <div className="row">
              <button type="button" className="primary" onClick={() => navigate(study ? `/studies/${study.id}` : "/studies")}>
                Back to study
              </button>
              <button type="button" className="ghost" onClick={() => navigate("/history")}>
                View history
              </button>
            </div>
          </section>
        )}
      </div>
    </>
  );
}
